<template>
  <public-layout>

    <!-- ═══════ BANNER ═══════ -->
    <div class="bl-banner">
      <div class="bl-banner__dots"></div>
      <div class="sc-container bl-banner__inner">
        <div class="bl-banner__left">
          <p class="bl-eyebrow">Content Management</p>
          <h1 class="bl-banner__title">My Blog Posts</h1>
          <p class="bl-banner__sub">Write, manage, and publish your construction insights.</p>
          <div class="bl-stats">
            <div class="bl-stat">
              <span class="bl-stat__val">{{ pagination.total || details.length }}</span>
              <span class="bl-stat__lbl">Total</span>
            </div>
            <div class="bl-stat">
              <span class="bl-stat__val">{{ details.filter(d => d.status == 1).length }}</span>
              <span class="bl-stat__lbl">Published</span>
            </div>
            <div class="bl-stat">
              <span class="bl-stat__val">{{ details.filter(d => d.status == 0).length }}</span>
              <span class="bl-stat__lbl">Awaiting</span>
            </div>
          </div>
        </div>
        <button type="button" class="bl-new-btn" @click="showCreateModal = true">
          <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
          New Post
        </button>
      </div>
    </div>

    <!-- ═══════ MAIN ═══════ -->
    <section class="sc-container bl-main">

      <!-- Filter tabs -->
      <div class="bl-filters">
        <button class="bl-filter" :class="{ 'bl-filter--active': activeFilter === 'all' }" @click="activeFilter = 'all'">All Posts</button>
        <button class="bl-filter" :class="{ 'bl-filter--active': activeFilter === 'active' }" @click="activeFilter = 'active'">
          Published
          <span v-if="details.filter(d => d.status == 1).length" class="bl-badge bl-badge--ok">{{ details.filter(d => d.status == 1).length }}</span>
        </button>
        <button class="bl-filter" :class="{ 'bl-filter--active': activeFilter === 'awaiting' }" @click="activeFilter = 'awaiting'">
          Awaiting
          <span v-if="details.filter(d => d.status == 0).length" class="bl-badge bl-badge--warn">{{ details.filter(d => d.status == 0).length }}</span>
        </button>
        <button class="bl-filter" :class="{ 'bl-filter--active': activeFilter === 'inactive' }" @click="activeFilter = 'inactive'">Deactivated</button>
      </div>

      <!-- Blog grid -->
      <div v-if="filteredDetails.length" class="bl-grid">
        <article v-for="data in filteredDetails" :key="data.id" class="bl-card">
          <!-- Thumbnail -->
          <router-link :to="'/blog/' + data.slug" class="bl-card__thumb-link">
            <img v-if="data.image" :src="data.image" class="bl-card__thumb" alt="" />
            <img v-else-if="data.images && data.images.length" :src="data.images[0].path" class="bl-card__thumb" alt="" />
            <div v-else class="bl-card__thumb bl-card__nophoto">
              <svg width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
          </router-link>

          <!-- Status ribbon -->
          <div class="bl-card__ribbon">
            <span v-if="data.status == 0" class="bl-pill bl-pill--warn">Awaiting</span>
            <span v-else-if="data.status == 1" class="bl-pill bl-pill--ok">Published</span>
            <span v-else class="bl-pill bl-pill--err">Deactivated</span>
          </div>

          <!-- Body -->
          <div class="bl-card__body">
            <span v-if="data.category" class="bl-card__cat">{{ data.category.name }}</span>
            <router-link :to="'/blog/' + data.slug" class="bl-card__title">
              {{ data.title.length > 70 ? data.title.substring(0, 70) + '…' : data.title }}
            </router-link>
            <p class="bl-card__snippet">{{ stripHtml(data.description).substring(0, 105) }}…</p>
            <div class="bl-card__foot">
              <span class="bl-card__date">
                <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {{ formatDate2(data.created_at) }}
              </span>
              <div class="bl-card__actions">
                <router-link :to="'/blog/' + data.slug" class="bl-action bl-action--view" title="View post">
                  <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </router-link>
                <button type="button" class="bl-action bl-action--edit" title="Edit post" @click="openEditBlog(data)">
                  <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Empty state -->
      <div v-else class="bl-empty">
        <div class="bl-empty__icon">
          <svg width="52" height="52" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        </div>
        <h3>{{ activeFilter === 'all' ? 'No posts yet' : 'No posts here' }}</h3>
        <p>{{ activeFilter === 'all' ? "You haven't written any blog posts yet." : 'No posts match this filter.' }}</p>
        <button v-if="activeFilter === 'all'" type="button" class="btn-primary" @click="showCreateModal = true">Write your first post</button>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.lastPage > 1" class="bl-pagination">
        <button class="bl-pg-btn" :disabled="!pagination.prevPageUrl" @click="fetchData(pagination.prevPageUrl)">
          <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          Previous
        </button>
        <span class="bl-pg-info">Page {{ pagination.currentPage }} of {{ pagination.lastPage }}</span>
        <button class="bl-pg-btn" :disabled="!pagination.nextPageUrl" @click="fetchData(pagination.nextPageUrl)">
          Next
          <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>

    </section>

    <!-- ═══════ CREATE MODAL ═══════ -->
    <div v-if="showCreateModal" class="bl-overlay" @click.self="showCreateModal = false">
      <div class="bl-modal">
        <div class="bl-modal__hd">
          <div>
            <h2 class="bl-modal__title">New Blog Post</h2>
            <p class="bl-modal__sub">Share your knowledge with the community</p>
          </div>
          <button type="button" class="bl-modal__close" @click="showCreateModal = false">
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="bl-modal__body">
          <form id="blog-form">
            <div class="sc-field">
              <label>Posting as</label>
              <div class="radio-group">
                <label class="radio-chip"><input type="radio" v-model="blog.user_type" value="user" /><span>Personal</span></label>
                <label class="radio-chip"><input type="radio" v-model="blog.user_type" value="company" /><span>Company</span></label>
              </div>
            </div>
            <div v-if="blog.user_type === 'company'" class="sc-field">
              <label>Company <span class="req">*</span></label>
              <select name="user_id" v-model="blog.user_id">
                <option v-for="(c, i) in companies" :key="i" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <input v-else type="hidden" name="user_id" v-model="blog.user_id" />
            <input type="hidden" name="user_type" v-model="blog.user_type" />
            <div class="sc-field">
              <label>Title <span class="req">*</span></label>
              <input type="text" name="title" v-model="blog.title" placeholder="Enter a compelling title…" />
            </div>
            <div class="sc-field">
              <label>Category <span class="req">*</span></label>
              <select name="cat_id" v-model="blog.cat_id">
                <option value="" disabled>Select category</option>
                <option v-for="(c, i) in categories" :key="i" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="sc-field">
              <label>Content <span class="req">*</span></label>
              <QuillEditor v-model:content="blog.description" :toolbar="customToolbar" contentType="html" />
              <input type="hidden" name="description" :value="blog.description" />
            </div>
            <div class="sc-field">
              <label>YouTube Video URL</label>
              <input type="url" name="youtube_url" v-model="blog.youtube_url" placeholder="https://www.youtube.com/watch?v=..." />
            </div>
            <div class="sc-field">
              <label>Instagram Video URL</label>
              <input type="url" name="instagram_url" v-model="blog.instagram_url" placeholder="https://www.instagram.com/p/..." />
            </div>
            <div class="sc-field">
              <label>Images</label>
              <div class="img-upload-wrap">
                <vue-upload-multiple-image
                  @upload-success="uploadImage"
                  @before-remove="beforeRemove"
                  @edit-image="editImage"
                  @data-change="dataChange"
                  :data-images="images"
                  dragText="Drag images here"
                  browseText="Browse"
                  primaryText="added"
                  markIsPrimaryText="Set default"
                  popupText="Default image"
                  :maxImage="3"
                />
              </div>
            </div>
          </form>
        </div>
        <div class="bl-modal__ft">
          <button type="button" class="btn-ghost" @click="showCreateModal = false">Cancel</button>
          <button type="button" class="btn-primary" :disabled="loading" @click="createBlog()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Publish Post</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════ EDIT MODAL ═══════ -->
    <div v-if="showEditModal && currentData" class="bl-overlay" @click.self="closeUpdateModal()">
      <div class="bl-modal">
        <div class="bl-modal__hd">
          <div>
            <h2 class="bl-modal__title">Edit Post</h2>
            <p class="bl-modal__sub">Update content and images</p>
          </div>
          <button type="button" class="bl-modal__close" @click="closeUpdateModal()">
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="bl-modal__body">
          <form id="update-form" enctype="multipart/form-data">
            <div class="sc-field">
              <label>Posting as</label>
              <div class="radio-group">
                <label class="radio-chip"><input type="radio" v-model="currentData.user_type" value="user" name="user_type" /><span>Personal</span></label>
                <label class="radio-chip"><input type="radio" v-model="currentData.user_type" value="company" name="user_type" /><span>Company</span></label>
              </div>
            </div>
            <div v-if="currentData.user_type === 'company'" class="sc-field">
              <label>Company <span class="req">*</span></label>
              <select name="user_id" v-model="currentData.user_id">
                <option v-for="(c, i) in companies" :key="i" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <select v-else name="user_id" v-model="currentData.user_id" class="sr-only">
              <option :value="user_id">{{ user.name }}</option>
            </select>

            <div class="sc-field">
              <label>Title <span class="req">*</span></label>
              <input type="text" name="title" v-model="currentData.title" />
            </div>
            <div class="sc-field">
              <label>Category <span class="req">*</span></label>
              <select name="cat_id" v-model="currentData.cat_id">
                <option value="" disabled>Select category</option>
                <option v-for="(c, i) in categories" :key="i" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="sc-field">
              <label>Content <span class="req">*</span></label>
              <QuillEditor v-model:content="currentData.description" :toolbar="customToolbar" contentType="html" />
              <input type="hidden" name="description" :value="currentData.description" />
            </div>

            <div class="sc-field">
              <label>YouTube Video URL</label>
              <input type="url" name="youtube_url" v-model="currentData.youtube_url" placeholder="https://www.youtube.com/watch?v=..." />
            </div>
            <div class="sc-field">
              <label>Instagram Video URL</label>
              <input type="url" name="instagram_url" v-model="currentData.instagram_url" placeholder="https://www.instagram.com/p/..." />
            </div>

            <!-- Existing images -->
            <div v-if="currentData.images && currentData.images.length" class="sc-field">
              <label>Current Images</label>
              <div class="img-preview-row">
                <div v-for="img in currentData.images" :key="'exist-' + img.id" class="img-thumb">
                  <img :src="img.path" alt="" />
                  <button type="button" class="img-thumb__rm" @click="deleteBlogImage(img.id)" title="Remove">
                    <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Add new images -->
            <div class="sc-field">
              <label>Add New Images</label>
              <label class="img-pick-btn" for="images">
                <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Choose files
                <input type="file" id="images" name="uploads" multiple accept="image/*" @change="viewAddedImage" class="sr-only" />
              </label>
              <div v-if="view_images.length" class="img-preview-row" style="margin-top:.625rem">
                <div v-for="(img, i) in view_images" :key="'new-' + i" class="img-thumb">
                  <img :src="img.src" alt="" />
                  <button type="button" class="img-thumb__rm" @click="removeImage(i)" title="Remove">
                    <svg width="10" height="10" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <input type="hidden" name="id" v-model="currentData.id" />
          </form>
        </div>
        <div class="bl-modal__ft">
          <button type="button" class="btn-ghost" @click="closeUpdateModal()">Cancel</button>
          <button type="button" class="btn-primary" :disabled="loading" @click="updateBlog()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </div>
    </div>

  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import moment from 'moment';
import VueUploadMultipleImage from '@/components/VueUploadMultipleImage';

export default {
  name: 'UserBlog',
  components: { PublicLayout, QuillEditor, VueUploadMultipleImage },

  created() {
    this.fetchData();
    this.fetchUser();
    this.fetchCategories();
    this.fetchTotalProjects();
    this.fetchTotalFollowers();
    this.fetchCompanies();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        document.title = to.meta.title || 'My Blog Posts';
      },
    },
  },

  data() {
    return {
      loading: false,
      user_id: localStorage.getItem('user'),
      activeFilter: 'all',
      currentData: {},
      details: [],
      categories: [],
      companies: [],
      user: {},
      total_project: 0,
      total_followers: 0,
      pagination: {
        lastPage: 0,
        currentPage: 1,
        total: 0,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
      blog: {
        title: '',
        description: '',
        cat_id: '',
        images: '',
        user_type: 'user',
        user_id: localStorage.getItem('user'),
        youtube_url: '',
        instagram_url: '',
      },
      images: [],
      view_images: [],
      showCreateModal: false,
      showEditModal: false,
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
    };
  },

  computed: {
    image_url() {
      return process.env.VUE_APP_IMAGE_URL;
    },
    filteredDetails() {
      const map = { active: 1, awaiting: 0, inactive: 2 };
      if (this.activeFilter === 'all') return this.details;
      return this.details.filter(d => d.status == map[this.activeFilter]);
    },
  },

  methods: {
    fetchData(url) {
      const endpoint = url || process.env.VUE_APP_API_URL + 'blog/user/' + localStorage.getItem('user');
      axios(endpoint).then(response => {
        if (response.status !== 200) return;
        const data = response.data;
        this.details = data.data;
        this.configPagination(data);
      }).catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.user = response.data;
      }).catch(() => {});
    },

    fetchCompanies() {
      axios(process.env.VUE_APP_API_URL + 'user/get-companies/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.companies = response.data;
      }).catch(() => {});
    },

    fetchCategories() {
      axios(process.env.VUE_APP_API_URL + 'blog/categories').then(response => {
        if (response.status !== 200) return;
        this.categories = response.data;
      }).catch(() => {});
    },

    fetchTotalProjects() {
      axios(process.env.VUE_APP_API_URL + 'project/total-project/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.total_project = response.data;
      }).catch(() => {});
    },

    fetchTotalFollowers() {
      axios(process.env.VUE_APP_API_URL + 'user/total-following/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.total_followers = response.data;
      }).catch(() => {});
    },

    createBlog() {
      this.loading = true;
      var form = document.getElementById('blog-form');
      var _data = new FormData(form);
      axios.post(process.env.VUE_APP_API_URL + 'blog/create-blog', _data)
        .then(response => {
          if (response.status === 200) {
            this.uploadBlogImage(response.data, this.blog.images);
            this.showCreateModal = false;
            this.fetchData();
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    uploadBlogImage(id, imgs) {
      axios.post(process.env.VUE_APP_API_URL + 'blog/upload-image', { blog_id: id, images: imgs })
        .then(response => {
          if (response.status === 200) {
            this.blog.images = '';
            this.images = [];
            this.fetchData();
          }
        })
        .catch(() => {});
    },

    updateBlog() {
      this.loading = true;
      const _data = new FormData();
      _data.append('id', this.currentData.id);
      _data.append('title', this.currentData.title);
      _data.append('cat_id', this.currentData.cat_id);
      _data.append('user_type', this.currentData.user_type);
      _data.append('user_id', this.currentData.user_id);
      _data.append('description', this.currentData.description);
      if (this.currentData.video_id) _data.append('video_id', this.currentData.video_id);
      if (this.currentData.is_video) _data.append('is_video', this.currentData.is_video);
      this.view_images.forEach(img => _data.append('uploads[]', img.file));
      axios.post(process.env.VUE_APP_API_URL + 'blog/update-blog', _data)
        .then(() => {
          this.closeUpdateModal();
          this.fetchData();
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    deleteBlogImage(id) {
      axios(process.env.VUE_APP_API_URL + 'blog/delete-blog-image/' + id)
        .then(response => {
          if (response.status === 200) {
            this.currentData.images = this.currentData.images.filter(img => img.id !== id);
            this.fetchData();
          }
        })
        .catch(() => {});
    },

    openEditBlog(data) {
      this.currentData = { ...data, images: data.images ? [...data.images] : [] };
      this.view_images = [];
      this.showEditModal = true;
    },

    closeUpdateModal() {
      this.showEditModal = false;
      this.loading = false;
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },

    uploadImage(formData, index, fileList) { this.blog.images = fileList; },
    beforeRemove(index, done) { if (confirm('Remove image?')) done(); },
    editImage() {},
    dataChange() {},

    viewAddedImage(e) {
      const selectedFiles = e.target.files;
      for (let i = 0; i < selectedFiles.length; i++) {
        this.view_images.push({ src: URL.createObjectURL(selectedFiles[i]), file: selectedFiles[i] });
      }
    },

    removeImage(index) {
      this.view_images.splice(index, 1);
      const dt = new DataTransfer();
      const input = document.getElementById('images');
      if (input) {
        const { files } = input;
        for (let i = 0; i < files.length; i++) {
          if (index !== i) dt.items.add(files[i]);
        }
        input.files = dt.files;
      }
    },

    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },

    formatDate2(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },
};
</script>

<style scoped>
/* ── Banner ─────────────────────────────────────────────── */
.bl-banner {
  position: relative;
  margin-top: 68px;
  background: linear-gradient(135deg, #0d1f36 0%, #1e3a5f 55%, #1a4a7a 100%);
  padding: 4rem 1.5rem 3.5rem;
  overflow: hidden;
}
.bl-banner__dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 36px 36px;
}
.bl-banner::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
  height: 56px; background: var(--surface-2, #f8fafc);
  clip-path: ellipse(60% 100% at 50% 100%);
}
.bl-banner__inner {
  position: relative; z-index: 2;
  display: flex; align-items: flex-end; justify-content: space-between;
  flex-wrap: wrap; gap: 2rem;
}
.bl-banner__left { display: flex; flex-direction: column; gap: .625rem; }
.bl-eyebrow {
  font-size: .75rem; font-weight: 700; letter-spacing: .1em;
  text-transform: uppercase; color: var(--accent, #f59e0b); margin: 0;
}
.bl-banner__title {
  font-size: clamp(1.75rem, 3.5vw, 2.5rem); font-weight: 800;
  color: #fff; letter-spacing: -.03em; margin: 0; line-height: 1.1;
}
.bl-banner__sub {
  font-size: 1rem; color: rgba(255,255,255,.65); margin: 0;
}
.bl-stats {
  display: flex; gap: 1.75rem; margin-top: .5rem;
}
.bl-stat { display: flex; flex-direction: column; }
.bl-stat__val { font-size: 1.5rem; font-weight: 800; color: #fff; line-height: 1; }
.bl-stat__lbl { font-size: .75rem; color: rgba(255,255,255,.5); margin-top: .15rem; }

/* New post button */
.bl-new-btn {
  display: inline-flex; align-items: center; gap: .5rem;
  background: var(--accent, #f59e0b); color: #fff;
  border: none; border-radius: var(--radius-full, 9999px);
  padding: .75rem 1.5rem; font-size: .9375rem; font-weight: 700;
  cursor: pointer; white-space: nowrap; flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(245,158,11,.35);
  transition: background .15s, transform .15s, box-shadow .15s;
}
.bl-new-btn:hover { background: #d97706; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(245,158,11,.4); }

/* ── Main section ───────────────────────────────────────── */
.bl-main { padding: 2.5rem 0 4rem; }

/* ── Filters ────────────────────────────────────────────── */
.bl-filters {
  display: flex; align-items: center; gap: .375rem;
  flex-wrap: wrap; margin-bottom: 2rem;
  border-bottom: 1.5px solid var(--border-light, #f1f5f9);
  padding-bottom: 0;
}
.bl-filter {
  display: inline-flex; align-items: center; gap: .375rem;
  background: none; border: none; border-bottom: 2.5px solid transparent;
  padding: .625rem 1rem; font-size: .9rem; font-weight: 600;
  color: var(--text-muted, #6b7280); cursor: pointer;
  margin-bottom: -1.5px; transition: color .15s, border-color .15s;
}
.bl-filter:hover { color: var(--text, #111827); }
.bl-filter--active { color: var(--primary, #1e3a5f); border-bottom-color: var(--primary, #1e3a5f); }
.bl-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 .375rem;
  border-radius: 99px; font-size: .7rem; font-weight: 700;
}
.bl-badge--ok  { background: #d1fae5; color: #065f46; }
.bl-badge--warn { background: #fef3c7; color: #92400e; }
.bl-badge--err  { background: #fee2e2; color: #991b1b; }

/* ── Blog grid ──────────────────────────────────────────── */
.bl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}
.bl-card {
  background: #fff;
  border-radius: var(--radius-lg, 1.25rem);
  border: 1px solid var(--border-light, #f1f5f9);
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  overflow: hidden;
  display: flex; flex-direction: column;
  position: relative;
  transition: box-shadow .2s, transform .2s;
}
.bl-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,.09);
  transform: translateY(-2px);
}
.bl-card__thumb-link { display: block; flex-shrink: 0; }
.bl-card__thumb {
  width: 100%; height: 190px; object-fit: cover; display: block;
}
.bl-card__nophoto {
  height: 190px; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0f4f8, #e2eaf4);
  color: #94a3b8;
}
.bl-card__ribbon {
  position: absolute; top: 1rem; left: 1rem;
}
.bl-pill {
  display: inline-block; padding: .25rem .625rem;
  border-radius: var(--radius-full, 9999px);
  font-size: .7rem; font-weight: 700; letter-spacing: .03em;
  backdrop-filter: blur(8px);
}
.bl-pill--ok   { background: rgba(209,250,229,.9); color: #065f46; }
.bl-pill--warn { background: rgba(254,243,199,.9); color: #92400e; }
.bl-pill--err  { background: rgba(254,226,226,.9); color: #991b1b; }

.bl-card__body { padding: 1.125rem; display: flex; flex-direction: column; flex: 1; }
.bl-card__cat {
  display: inline-block; font-size: .7rem; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; color: var(--primary, #1e3a5f);
  margin-bottom: .375rem;
}
.bl-card__title {
  font-size: .9375rem; font-weight: 700; color: var(--text, #111827);
  text-decoration: none; line-height: 1.45; display: block; margin-bottom: .5rem;
}
.bl-card__title:hover { color: var(--primary, #1e3a5f); }
.bl-card__snippet {
  font-size: .8125rem; color: var(--text-muted, #6b7280);
  line-height: 1.55; margin: 0 0 1rem; flex: 1;
}
.bl-card__foot {
  display: flex; align-items: center; justify-content: space-between;
  border-top: 1px solid var(--border-light, #f1f5f9); padding-top: .75rem;
  margin-top: auto;
}
.bl-card__date {
  display: flex; align-items: center; gap: .3rem;
  font-size: .75rem; color: var(--text-muted, #6b7280);
}
.bl-card__actions { display: flex; align-items: center; gap: .375rem; }
.bl-action {
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: var(--radius, .625rem);
  border: 1.5px solid var(--border, #e5e7eb);
  background: none; cursor: pointer;
  color: var(--text-muted, #6b7280); text-decoration: none;
  transition: background .15s, color .15s, border-color .15s;
}
.bl-action:hover { background: var(--surface-2, #f8fafc); color: var(--primary, #1e3a5f); border-color: var(--primary, #1e3a5f); }
.bl-action--edit:hover { color: var(--accent, #f59e0b); border-color: var(--accent, #f59e0b); }

/* ── Empty state ────────────────────────────────────────── */
.bl-empty {
  display: flex; flex-direction: column; align-items: center;
  gap: 1rem; padding: 5rem 1rem; text-align: center;
  color: var(--text-muted, #6b7280);
}
.bl-empty__icon {
  width: 88px; height: 88px; border-radius: 50%;
  background: var(--surface-3, #f1f5f9);
  display: flex; align-items: center; justify-content: center;
  color: #cbd5e1;
}
.bl-empty h3 { font-size: 1.125rem; font-weight: 700; color: var(--text, #111827); margin: 0; }
.bl-empty p  { font-size: .9375rem; margin: 0; max-width: 360px; }

/* ── Pagination ─────────────────────────────────────────── */
.bl-pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 1rem; padding: 2.5rem 0 0;
}
.bl-pg-btn {
  display: inline-flex; align-items: center; gap: .375rem;
  background: #fff; border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius, .625rem); padding: .5rem 1.125rem;
  font-size: .875rem; font-weight: 600; color: var(--text, #111827);
  cursor: pointer; transition: background .15s, border-color .15s;
}
.bl-pg-btn:disabled { opacity: .4; cursor: not-allowed; }
.bl-pg-btn:not(:disabled):hover { background: var(--surface-2, #f8fafc); border-color: var(--primary, #1e3a5f); }
.bl-pg-info { font-size: .875rem; color: var(--text-muted, #6b7280); }

/* ── Modals ─────────────────────────────────────────────── */
.bl-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.55);
  z-index: 800; display: flex; align-items: center; justify-content: center;
  padding: 1rem; backdrop-filter: blur(3px);
}
.bl-modal {
  background: #fff; border-radius: var(--radius-lg, 1.25rem);
  box-shadow: 0 24px 64px rgba(0,0,0,.16);
  width: 100%; max-width: 700px; max-height: 92vh; overflow-y: auto;
  display: flex; flex-direction: column;
}
.bl-modal__hd {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 1.5rem 1.75rem 1.25rem;
  border-bottom: 1px solid var(--border-light, #f1f5f9);
  flex-shrink: 0;
}
.bl-modal__title { font-size: 1.125rem; font-weight: 800; color: var(--text, #111827); margin: 0 0 .2rem; }
.bl-modal__sub   { font-size: .8125rem; color: var(--text-muted, #6b7280); margin: 0; }
.bl-modal__close {
  background: var(--surface-2, #f8fafc); border: none; cursor: pointer;
  color: var(--text-muted, #6b7280); border-radius: .5rem;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background .15s, color .15s;
}
.bl-modal__close:hover { background: var(--surface-3, #f1f5f9); color: var(--text, #111827); }
.bl-modal__body { padding: 1.5rem 1.75rem; flex: 1; }
.bl-modal__ft {
  padding: 1.125rem 1.75rem; border-top: 1px solid var(--border-light, #f1f5f9);
  display: flex; justify-content: flex-end; gap: .75rem; flex-shrink: 0;
}

/* ── Form fields ────────────────────────────────────────── */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1.125rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #374151; }
.sc-field input[type="text"],
.sc-field input[type="email"],
.sc-field select,
.sc-field textarea {
  width: 100%; padding: .625rem .875rem;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius, .625rem);
  font-family: inherit; font-size: .9375rem;
  color: var(--text, #111827); background: #fff;
  outline: none; transition: border-color .15s, box-shadow .15s;
  box-sizing: border-box;
}
.sc-field input:focus,
.sc-field select:focus { border-color: var(--primary, #1e3a5f); box-shadow: 0 0 0 3px rgba(30,58,95,.08); }
.req { color: var(--danger, #ef4444); }

.radio-group { display: flex; gap: .625rem; flex-wrap: wrap; }
.radio-chip {
  display: flex; align-items: center; gap: .375rem; cursor: pointer;
  font-size: .875rem; font-weight: 500; color: var(--text-muted, #6b7280);
  border: 1.5px solid var(--border, #e5e7eb);
  padding: .375rem .875rem; border-radius: var(--radius-full, 9999px);
  transition: border-color .15s, color .15s;
}
.radio-chip:has(input:checked) { border-color: var(--primary, #1e3a5f); color: var(--primary, #1e3a5f); background: rgba(30,58,95,.04); }
.radio-chip input { display: none; }

/* Image upload area */
.img-upload-wrap { display: flex; justify-content: center; }

/* Image file picker button */
.img-pick-btn {
  display: inline-flex; align-items: center; gap: .5rem;
  padding: .5rem 1.125rem; border-radius: var(--radius, .625rem);
  border: 1.5px dashed var(--border, #e5e7eb);
  font-size: .875rem; font-weight: 600; color: var(--text-muted, #6b7280);
  cursor: pointer; background: var(--surface-2, #f8fafc);
  transition: border-color .15s, color .15s, background .15s;
}
.img-pick-btn:hover { border-color: var(--primary, #1e3a5f); color: var(--primary, #1e3a5f); background: rgba(30,58,95,.04); }

/* Image preview row */
.img-preview-row { display: flex; flex-wrap: wrap; gap: .625rem; }
.img-thumb {
  position: relative; width: 76px; height: 76px;
  border-radius: var(--radius, .625rem); overflow: visible;
}
.img-thumb img {
  width: 100%; height: 100%; object-fit: cover;
  border-radius: var(--radius, .625rem);
  border: 1.5px solid var(--border-light, #f1f5f9);
}
.img-thumb__rm {
  position: absolute; top: -7px; right: -7px;
  background: var(--danger, #ef4444); color: #fff;
  border: 2px solid #fff; border-radius: 50%;
  width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; padding: 0;
}

/* Utility */
.sr-only {
  position: absolute; width: 1px; height: 1px;
  padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0,0,0,0); border: 0;
}

/* Buttons */
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
  background: var(--primary, #1e3a5f); color: #fff;
  border: none; border-radius: var(--radius-full, 9999px);
  padding: .625rem 1.375rem; font-size: .9375rem; font-weight: 700;
  cursor: pointer; text-decoration: none;
  transition: background .15s, transform .1s;
}
.btn-primary:hover:not(:disabled) { background: #162d4a; transform: translateY(-1px); }
.btn-primary:disabled { opacity: .55; cursor: not-allowed; }
.btn-ghost {
  display: inline-flex; align-items: center; justify-content: center;
  background: none; color: var(--text-muted, #6b7280);
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius-full, 9999px);
  padding: .625rem 1.375rem; font-size: .9375rem; font-weight: 600;
  cursor: pointer; transition: background .15s, color .15s, border-color .15s;
}
.btn-ghost:hover { background: var(--surface-2, #f8fafc); color: var(--text, #111827); }

.sc-spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

:deep(.ql-editor) { min-height: 160px; }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .bl-banner { padding: 3rem 1.25rem 2.5rem; }
  .bl-banner__inner { flex-direction: column; align-items: flex-start; }
  .bl-new-btn { width: 100%; justify-content: center; }
  .bl-stats { gap: 1.25rem; }
  .bl-grid { grid-template-columns: 1fr; }
  .bl-modal { max-height: 100vh; border-radius: 1rem 1rem 0 0; align-self: flex-end; }
  .bl-modal__hd, .bl-modal__body, .bl-modal__ft { padding-left: 1.25rem; padding-right: 1.25rem; }
}
</style>
